import { Component, type ErrorInfo, type ReactNode } from "react";
import { useLocation } from "react-router-dom";

const RELOAD_FLAG = "app-chunk-reloaded";

/** True for stale/failed dynamic-import (lazy chunk) failures. */
function isChunkLoadError(error: Error): boolean {
  const msg = `${error?.name ?? ""} ${error?.message ?? ""}`.toLowerCase();
  return (
    msg.includes("dynamically imported module") ||
    msg.includes("failed to fetch dynamically") ||
    msg.includes("chunkloaderror") ||
    msg.includes("importing a module script failed") ||
    msg.includes("error loading dynamically imported module")
  );
}

type Props = {
  /** Short label for the area (e.g. "Today tab"). Shown in the fallback. */
  label?: string;
  /** Custom fallback renderer; receives error + reset. */
  fallback?: (args: { error: Error; reset: () => void }) => ReactNode;
  children: ReactNode;
};

type State = { error: Error | null };

/**
 * Generic React error boundary. Wrap an area of the app to make sure a
 * crash inside doesn't take down the rest of the dashboard.
 *
 * Used per-tab in src/pages/Index.tsx so Today / Food / Train / Posing / Me
 * fail independently.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error(`[ErrorBoundary${this.props.label ? ` ${this.props.label}` : ""}]`, error, info);

    // A stale lazy-loaded chunk (old tab open across a deploy) throws on import.
    // Reload once to pick up the new build instead of showing a dead screen.
    if (isChunkLoadError(error) && !sessionStorage.getItem(RELOAD_FLAG)) {
      sessionStorage.setItem(RELOAD_FLAG, "1");
      window.location.reload();
    }
  }

  reset = () => {
    sessionStorage.removeItem(RELOAD_FLAG);
    this.setState({ error: null });
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    if (this.props.fallback) {
      return this.props.fallback({ error, reset: this.reset });
    }
    const stale = isChunkLoadError(error);
    return (
      <div
        role="alert"
        className="m-4 rounded-xl border border-border bg-card p-5 text-sm leading-relaxed text-foreground"
      >
        <div className="mb-1.5 font-bold text-destructive">
          {stale
            ? "A newer version of the app is available."
            : `Something went wrong${this.props.label ? ` in ${this.props.label}` : ""}.`}
        </div>
        <div className="mb-3 text-foreground/80">
          {stale
            ? "Reload to get the latest version."
            : "The rest of the app is still working. You can try again or switch tabs."}
        </div>
        {error.message ? (
          <div className="mb-3 break-words font-mono text-xs text-foreground/60">{error.message}</div>
        ) : null}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={this.reset}
            className="rounded-lg bg-primary px-3.5 py-2 font-semibold text-primary-foreground"
          >
            Try again
          </button>
          <button
            onClick={() => {
              sessionStorage.removeItem(RELOAD_FLAG);
              window.location.reload();
            }}
            className="rounded-lg border border-border px-3.5 py-2 font-semibold text-foreground"
          >
            Reload app
          </button>
        </div>
      </div>
    );
  }
}

/** ErrorBoundary that clears itself whenever the route changes. */
export function RouteErrorBoundary({ children }: { children: ReactNode }) {
  const location = useLocation();
  return <ErrorBoundary key={location.pathname}>{children}</ErrorBoundary>;
}

export default ErrorBoundary;
